/* eslint-disable react/prop-types */
import { createContext, useContext, useState, useEffect } from 'react';
import axiosInstance from '@/utils/axiosInstance';
import { API_URL, USER_ROLES } from '@/constant/constant';
import { useUser } from '@/contexts/UserContext';

const OrderContext = createContext({});

export function OrderProvider({ children }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [orderCount, setOrderCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const { user, isInitialized } = useUser();

  const fetchOrders = async (reset = false) => {
    if (!user?.id) return;
    if (loading || loadingMore) return;

    const pageToFetch = reset ? 0 : currentPage;

    try {
      if (reset) {
        setLoading(true);
        setCurrentPage(0);
        setHasMore(true);
      } else {
        setLoadingMore(true);
      }

      const url = `${API_URL.BASE_ORDER}/rest/big-local/api/v1/orders/vendor/all?pageNo=${pageToFetch}`;

      const res = await axiosInstance.get(url, {
        headers: {
          sessionToken: localStorage.getItem('sessionToken'),
          'X-USER-ROLE': USER_ROLES.VENDOR,
          'X-Vendor-Id': user.id,
        },
      });

      const newOrders = res?.data || [];

      if (!Array.isArray(newOrders)) {
        if (reset) {
          setOrders([]);
          setOrderCount(0);
        }
        setHasMore(false);
        return;
      }

      // If no orders returned or less than 10, we've reached the end
      if (newOrders.length === 0 || newOrders.length < 10) {
        setHasMore(false);
      }

      if (reset) {
        // Check for new pending orders
        setOrders(newOrders);
        setOrderCount(newOrders.length);
        setCurrentPage(1);
      } else {
        // Append new orders
        setOrders((prev) => [...prev, ...newOrders]);
        setOrderCount((prev) => prev + newOrders.length);
        setCurrentPage((prev) => prev + 1);
      }

      // Early return if no data
      if (newOrders.length === 0) {
        return;
      }
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      if (reset) {
        setOrders([]);
      }
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreOrders = async () => {
    if (!hasMore || loadingMore || loading) return;
    await fetchOrders(false);
  };

  const clearOrders = () => {
    setOrders([]);
    setOrderCount(0);
    setCurrentPage(0);
    setHasMore(true);
    setLoading(false);
    setLoadingMore(false);
  };

  // Listen for logout events
  useEffect(() => {
    const handleLogout = () => {
      clearOrders();
    };

    window.addEventListener('user-logout', handleLogout);

    return () => {
      window.removeEventListener('user-logout', handleLogout);
    };
  }, []);

  // Fetch orders when user is available and initialized
  useEffect(() => {
    if (isInitialized && user?.id) {
      fetchOrders(true);
    }
  }, [user?.id, isInitialized]);

  const value = {
    orders,
    loading,
    loadingMore,
    orderCount,
    hasMore,
    fetchOrders: () => fetchOrders(true),
    loadMoreOrders,
    clearOrders,
  };

  return <OrderContext.Provider value={value}>{children}</OrderContext.Provider>;
}

export const useOrders = () => {
  const context = useContext(OrderContext);
  if (context === undefined) {
    throw new Error('useOrders must be used within an OrderProvider');
  }
  return context;
};
